/** WatchTV — partner-feed waiting room.
 *  TV streams are not yet live (no partner deals signed). This page captures
 *  email leads for the launch and shows the planned channel lineup. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { T } from "@/lib/theme";

const C = T.dark;
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const TV_STATIONS = [
  { id: "gtv", name: "GTV", country: "Ghana", licensee: "Ghana Broadcasting Corporation", genre: "General" },
  { id: "tv3-gh", name: "TV3 Ghana", country: "Ghana", licensee: "Media General Ghana", genre: "General" },
  { id: "joy-news", name: "JoyNews", country: "Ghana", licensee: "Multimedia Group", genre: "News" },
  { id: "citi-tv", name: "Citi TV", country: "Ghana", licensee: "Omni Media", genre: "General" },
  { id: "channels-ng", name: "Channels TV", country: "Nigeria", licensee: "Channels Inc.", genre: "News" },
  { id: "nta", name: "NTA", country: "Nigeria", licensee: "Nigerian Television Authority", genre: "General" },
  { id: "rti", name: "RTI", country: "Cote d'Ivoire", licensee: "Radiodiffusion Télévision Ivoirienne", genre: "General" },
  { id: "rts", name: "RTS", country: "Senegal", licensee: "Radiodiffusion Télévision Sénégalaise", genre: "General" },
];

const COUNTRY_FLAGS = { Ghana: "🇬🇭", Nigeria: "🇳🇬", "Cote d'Ivoire": "🇨🇮", Senegal: "🇸🇳" };

export default function WatchTV() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("Ghana");
  const [status, setStatus] = useState({ loading: false, msg: "" });

  const subscribe = async (e) => {
    e?.preventDefault?.();
    if (!email || !/.+@.+\..+/.test(email)) { setStatus({ loading: false, msg: "Enter a valid email." }); return; }
    setStatus({ loading: true, msg: "" });
    try {
      const r = await axios.post(`${API_BASE}/free/newsletter/subscribe`,
        { email, country, source: "tv-waitlist" });
      setStatus({ loading: false, msg: r.data?.message || "Check your inbox to confirm." });
      setEmail("");
    } catch (err) {
      setStatus({ loading: false, msg: err.response?.data?.detail || "Something went wrong." });
    }
  };

  return (
    <div data-testid="watchtv-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`body { margin:0; background:${C.bg} } @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap')`}</style>

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <a href="/" data-testid="tv-logo" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
          <span style={{ color: C.muted, fontSize: 12, fontWeight: 400, marginLeft: 10 }}>TV · waitlist</span>
        </a>
        <button data-testid="tv-back-listen" onClick={() => nav("/listen")}
          style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
            padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          📻 Radio
        </button>
      </div>

      <div style={{ padding: "48px 28px", maxWidth: 920, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ color: T.greenAccent, fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", marginBottom: 14 }}>
            📺 IN DEVELOPMENT — PARTNER DEALS IN PROGRESS
          </div>
          <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 42, fontWeight: 800, margin: "0 0 12px", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            West African TV, measured in real time.
          </h1>
          <p style={{ color: C.muted, fontSize: 16, lineHeight: 1.6, maxWidth: 620, margin: "0 auto 32px" }}>
            We're rolling out partner deals with the 8 largest broadcasters across Ghana, Nigeria, Côte d'Ivoire,
            and Senegal. <b style={{ color: C.text }}>Drop your email below and you'll be first in line</b> the moment a
            channel goes live.
          </p>

          <form onSubmit={subscribe} style={{ display: "flex", gap: 8, maxWidth: 480, margin: "0 auto", flexWrap: "wrap", justifyContent: "center" }}>
            <input data-testid="tv-waitlist-email" type="email" placeholder="you@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              style={{ flex: "1 1 240px", padding: "13px 16px", background: C.surface,
                border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontSize: 14, fontFamily: "inherit", outline: "none" }} />
            <select data-testid="tv-waitlist-country" value={country} onChange={(e) => setCountry(e.target.value)}
              style={{ padding: "13px 12px", background: C.surface, border: `1px solid ${C.border}`,
                color: C.text, borderRadius: 10, fontSize: 13, fontFamily: "inherit" }}>
              {Object.keys(COUNTRY_FLAGS).map((c) => <option key={c} value={c}>{COUNTRY_FLAGS[c]} {c}</option>)}
            </select>
            <button data-testid="tv-waitlist-submit" type="submit" disabled={status.loading}
              style={{ padding: "13px 24px", background: T.greenAccent, color: "#fff", border: "none",
                borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700 }}>
              {status.loading ? "..." : "Notify me →"}
            </button>
          </form>
          {status.msg && (
            <div data-testid="tv-waitlist-msg" style={{ color: T.greenAccent, fontSize: 13, marginTop: 14 }}>
              {status.msg}
            </div>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 36 }}>
          <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", textAlign: "center", marginBottom: 20 }}>
            Planned lineup
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
            {TV_STATIONS.map((s) => (
              <div key={s.id} data-testid={`tv-card-${s.id}`}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                  <span data-testid={`tv-soon-${s.id}`} style={{ padding: "4px 10px", background: T.greenAccent + "22",
                    border: `1px solid ${T.greenAccent}55`, borderRadius: 6,
                    fontSize: 10, fontWeight: 700, color: T.greenAccent, display: "inline-block", letterSpacing: "0.06em" }}>
                    WAITLIST OPEN
                  </span>
                </div>
                <div style={{ color: C.muted, fontSize: 11 }}>
                  {COUNTRY_FLAGS[s.country]} {s.country} · {s.genre}
                </div>
                <div style={{ color: C.muted, fontSize: 10, marginTop: 4, opacity: 0.7 }}>{s.licensee}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
